import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, useMediaQuery, useTheme, Drawer } from '@mui/material';
import { Chat as ChatIcon, Menu as MenuIcon } from '@mui/icons-material';
import PolicyChatPanel from './PolicyChatPanel';
import ConversationSidebar from './ConversationSidebar';
import { usePolicyChatStream } from './usePolicyChatStream';
import { useChatHistory } from './useChatHistory';
import { useMedNecContext } from '../hooks/useMedNecContext';
import type { ChatMessage } from './MessageBubble';

export const MedNecChat: React.FC = () => {
  const { company, center, isConfigured } = useMedNecContext();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmall);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const {
    sendMessage,
    sendMessageWithDocument,
    resetChat,
    isStreaming,
    streamingText,
    conversationId,
    setConversationId,
    error,
    lastTokenUsage,
  } = usePolicyChatStream();

  const {
    conversations,
    setConversations,
    isLoading: historyLoading,
    loadConversation,
    renameConversation,
    deleteConversation,
    refetch,
  } = useChatHistory();

  // Optimistically add new conversation to sidebar when backend assigns an ID
  const prevConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (conversationId && conversationId !== prevConversationIdRef.current) {
      prevConversationIdRef.current = conversationId;
      if (!conversations.find(c => c.id === conversationId)) {
        setConversations(prev => [{
          id: conversationId,
          title: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messageCount: 0,
        }, ...prev]);
      }
    }
  }, [conversationId, conversations, setConversations]);

  // When streaming finishes, commit the assistant message
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming && streamingText) {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: streamingText,
      }]);
      // Refetch to get updated title/message count
      refetch();
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming, streamingText, refetch]);

  const handleSelectConversation = useCallback(async (id: string) => {
    if (isStreaming) return;
    try {
      const loaded = await loadConversation(id);
      setMessages(loaded);
      setConversationId(id);
      if (isSmall) setSidebarOpen(false);
    } catch {
      // Failed to load — ignore
    }
  }, [isStreaming, loadConversation, setConversationId, isSmall]);

  const handleNewChat = useCallback(() => {
    if (isStreaming) return;
    resetChat();
    setMessages([]);
    if (isSmall) setSidebarOpen(false);
  }, [isStreaming, resetChat, isSmall]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteConversation(id);
    if (id === conversationId) {
      resetChat();
      setMessages([]);
    }
  }, [deleteConversation, conversationId, resetChat]);

  if (!isConfigured) {
    return (
      <Box sx={{ width: '100%', p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography color="text.secondary">
          Please select a company and center to use the policy review chat.
        </Typography>
      </Box>
    );
  }

  const sidebarContent = (
    <ConversationSidebar
      conversations={conversations}
      activeConversationId={conversationId}
      isLoading={historyLoading}
      onSelectConversation={handleSelectConversation}
      onNewChat={handleNewChat}
      onRename={renameConversation}
      onDelete={handleDelete}
      disabled={isStreaming}
    />
  );

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Tooltip title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}>
            <IconButton size="small" onClick={() => setSidebarOpen(!sidebarOpen)} edge="start">
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <ChatIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Policy Review Chat</Typography>
            <Typography variant="caption" color="text.secondary">
              {company} — {center}
            </Typography>
          </Box>
        </Box>

        {/* Body: Sidebar + Chat */}
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Sidebar — drawer on small screens, inline on larger */}
          {isSmall ? (
            <Drawer
              variant="temporary"
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{ '& .MuiDrawer-paper': { width: 280, position: 'relative' } }}
            >
              {sidebarContent}
            </Drawer>
          ) : (
            sidebarOpen && sidebarContent
          )}

          {/* Chat Panel */}
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <PolicyChatPanel
              sendMessage={sendMessage}
              sendMessageWithDocument={sendMessageWithDocument}
              isStreaming={isStreaming}
              streamingText={streamingText}
              error={error}
              lastTokenUsage={lastTokenUsage}
              messages={messages}
              setMessages={setMessages}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MedNecChat;
